/**
 * localStorage-backed rolling memory. Mirrors `KokoMemoryManager.swift`:
 *
 *   - Entries are single lines of the form `- [YYYY-MM-DD HH:mm] {summary}`
 *     matching the native file format exactly.
 *   - Max 50 entries; oldest trimmed on append.
 *   - `loadForPrompt()` returns the full memory block for injection
 *     into Claude's system prompt.
 *   - Gated by the `memoryEnabled` setting; returns empty when off so
 *     downstream code can always call `loadForPrompt()` unconditionally.
 *
 * Unlike native's `memory.md` file on disk, web memory is per-browser-
 * origin. If the user clears site data they clear memory too — that's
 * the platform trade-off we accepted during planning.
 */

import type { SettingsStore } from "../app/SettingsStore";

const STORAGE_KEY = "koko_memory_v1";
const MEMORY_HEADER =
  "# Koko's memory\n\n" +
  "One-line summaries of past interactions — injected into Koko's system prompt.\n" +
  "Each line: `- [YYYY-MM-DD HH:mm] fact Koko learned`.\n" +
  "Oldest entries are trimmed when the list exceeds 50.\n" +
  "You can edit this by hand — Koko will read your edits on the next turn.\n\n";
const MAX_ENTRIES = 50;

export class KokoMemoryStore {
  private readonly settings: SettingsStore;

  constructor(settings: SettingsStore) {
    this.settings = settings;
    this.ensureHeaderPresent();
  }

  /**
   * Full memory content for system-prompt injection. Empty string when
   * disabled so `buildSystemPrompt` can short-circuit.
   */
  loadForPrompt(): string {
    if (!this.settings.get("memoryEnabled")) return "";
    const raw = this.readRaw();
    const trimmed = raw.trim();
    return trimmed.length === 0 ? "" : trimmed;
  }

  /**
   * Returns the raw memory text (header + entries). Used by the editor
   * panel so the user can see and modify exactly what the prompt sees.
   */
  readRawForEditor(): string {
    return this.readRaw();
  }

  /**
   * Replaces the entire memory blob. Called by the editor panel when
   * the user hits "Save".
   */
  writeRaw(rawText: string): void {
    try {
      window.localStorage.setItem(STORAGE_KEY, rawText);
    } catch {
      // localStorage quota exceeded or privacy mode; drop silently.
    }
  }

  /**
   * Appends one distillation entry (10–15 word summary). Called after
   * a turn completes, if the summary came back non-empty.
   */
  appendSummary(summary: string): void {
    if (!this.settings.get("memoryEnabled")) return;
    const trimmedSummary = summary.trim();
    if (trimmedSummary.length === 0) return;

    const timestamp = formatTimestampForEntry(new Date());
    const newEntry = `- [${timestamp}] ${trimmedSummary}`;

    const currentRaw = this.readRaw();
    const combined = currentRaw.endsWith("\n")
      ? currentRaw + newEntry + "\n"
      : currentRaw + "\n" + newEntry + "\n";

    this.writeRaw(trimOldestEntries(combined));
  }

  /** Wipes all entries but retains the header so the file stays self-describing. */
  clear(): void {
    this.writeRaw(MEMORY_HEADER);
  }

  /** Exposed for tests so we can drive the store deterministically. */
  static readonly STORAGE_KEY = STORAGE_KEY;

  private readRaw(): string {
    try {
      const stored = window.localStorage.getItem(STORAGE_KEY);
      return stored ?? MEMORY_HEADER;
    } catch {
      return MEMORY_HEADER;
    }
  }

  private ensureHeaderPresent(): void {
    const existing = this.readRaw();
    if (existing.trim().length === 0) {
      this.writeRaw(MEMORY_HEADER);
    }
  }
}

function formatTimestampForEntry(now: Date): string {
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  const hour = String(now.getHours()).padStart(2, "0");
  const minute = String(now.getMinutes()).padStart(2, "0");
  return `${year}-${month}-${day} ${hour}:${minute}`;
}

/**
 * When the entry list exceeds `MAX_ENTRIES`, drop the oldest entry
 * lines while preserving the header and any non-entry lines the user
 * may have added between entries (matching native's trim behavior).
 */
function trimOldestEntries(rawText: string): string {
  const lines = rawText.split("\n");
  const entryLineIndices: number[] = [];
  lines.forEach((line, index) => {
    if (/^- \[\d{4}-\d{2}-\d{2} \d{2}:\d{2}\]/.test(line)) {
      entryLineIndices.push(index);
    }
  });
  if (entryLineIndices.length <= MAX_ENTRIES) return rawText;

  const entriesToRemoveCount = entryLineIndices.length - MAX_ENTRIES;
  const indicesToRemove = new Set(entryLineIndices.slice(0, entriesToRemoveCount));
  const survivingLines = lines.filter((_, index) => !indicesToRemove.has(index));
  return survivingLines.join("\n");
}
